import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Check,
  CheckCircle,
  Copy,
  Info,
  Palette,
  PartyPopper,
  Pencil,
  PencilLine,
} from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Pressable } from "react-native";
import { TouchableOpacity } from "react-native-gesture-handler";
import uuid from "react-native-uuid";
import { Circle, View, XStack, YStack } from "tamagui";
import {
  ModuleDoneType,
  ModuleType,
  PlaniraPlanImage,
} from "../../templates/types";
import { useApi } from "../core/apis";
import { useUploadCover } from "../core/hooks/useUploadCover";
import { useStores } from "../core/stores";
import { TagColorsAvailable } from "../core/tags/types";
import { PlanInfoBox } from "../plans/PlanInfoBox";
import { PlanQuickstartOutroInfo } from "../plans/PlanQuickstartAddStep";
import { Body, BodyTextarea } from "../ui/Body";
import { Box } from "../ui/Box";
import { Button } from "../ui/Button";
import { EmojiPicker } from "../ui/EmojiPicker";
import { Footer } from "../ui/Footer";
import { Input } from "../ui/InputNew";
import { Label } from "../ui/Label";
import { FullScreenLoading } from "../ui/Loading";
import { Pill, PillType } from "../ui/Pill";
import { Screen } from "../ui/Screen";
import { Separator } from "../ui/Separator";
import { Title, TitleTextarea } from "../ui/Title";
import { EditorForm } from "../ui/YooptaEditor";
import type { PlansTemplateModelData } from "./PlansTemplateModel";

type Props = NativeStackScreenProps<ReactNavigation.RootParamList, "Template">;

function createDefaultPlanTemplateData(): Omit<PlansTemplateModelData, "uid"> {
  return {
    editHash: uuid.v4(),
    createdAt: Date.now(),
    timesUsed: 0,
    template: {
      type: "NotUsed",
      slug: "NotUSed",

      title: "My best title", // Set plan title as well
      description: "", // Not the same as planinfo desc...
      longDescription: "", // Only used in Gatsby...
      emoji: "🎉",
      titleHelp: "",
      imageType: PlaniraPlanImage.Party,

      suggestedTitles: [],

      modules: [
        {
          type: ModuleType.Calendar,
          doneType: ModuleDoneType.Voting,
          title: "When?",
          emoji: "📅",

          settings: {
            groupDays: false,
          },
        },
      ],

      onTemplateComplete: {
        outroTitle: "You are all set!",
        outroDescription: "Fantastic work! You are all set for this time.",
      },
    },
  };
}

const WidgetPreview = ({
  widgetTitle,
  uid,
}: {
  widgetTitle: string;
  uid: string;
}) => {
  React.useEffect(() => {
    // Create the script tag
    const script = document.createElement("script");
    script.src = "https://planira.app/widget.js";
    script.setAttribute("data-text", widgetTitle);
    script.setAttribute("data-uid", uid);
    script.async = true;

    // Append the script to the DOM
    const scriptContainer = document.getElementById("widget-container");
    if (scriptContainer) {
      scriptContainer.appendChild(script);
    }

    // Clean up the script when the component is unmounted
    return () => {
      if (scriptContainer) {
        scriptContainer.innerHTML = ""; // Remove injected script and any added elements
      }
    };
  }, [widgetTitle, uid]);

  return (
    <View minHeight={80}>
      <div id="widget-container" />
    </View>
  );
};

export const TemplateScreen = observer(({ route, navigation }: Props) => {
  const templateEditHash = route.params?.templateUid;
  const { templatesApi } = useApi();
  const { templatesStore } = useStores();
  const toast = useToastController();

  const model = templateEditHash
    ? templatesStore.byEditHash(templateEditHash)
    : null;
  const [showColors, setShowColors] = React.useState(false);
  const [showEmojiSheet, setSetEmojiSheet] = useState(false);
  const [widgetTitle, setWidgetTitle] = useState("Find dates");

  const selectCoverButton = useUploadCover(
    () => {},
    async (coverUrl) => {
      return model?.setTemplateData({ coverUrl, backgroundColor: null });
    },
    model?.uid,
  );

  React.useEffect(() => {
    if (!templateEditHash && !model) {
      templatesApi.add(createDefaultPlanTemplateData()).then((newTemplate) => {
        navigation.navigate("Template", {
          templateUid: newTemplate.data.editHash,
        });
      });
    } else if (templateEditHash && !model) {
      void templatesApi.getByEditHash(templateEditHash).then((_e) => {
        // If not found, show error TODO
      });
    }
  }, [templatesApi, templateEditHash, model, navigation]);

  if (!model) {
    return <FullScreenLoading />;
  }

  const urlToShare = __DEV__
    ? `http://localhost:8081/plan/create/${model.uid}`
    : `https://planira.app/plan/create/${model.uid}`;

  const widgetCode = `<script src="https://planira.app/widget.js"
        data-text='${widgetTitle}'
        data-uid='${model.data.uid}'></script>`;

  return (
    <Screen>
      <YStack gap="$larger">
        <Box>
          <Title>
            <PartyPopper /> Create Planira Template
          </Title>

          <Body>
            Great! Let's edit the details until perfection, then all you need is
            to share the link found in the bottom. 🔥
          </Body>
        </Box>

        <Box>
          <Title>1. It's all about the context!</Title>

          <Body>
            Set the context for your users using your Planira, make it shine ✨
          </Body>
        </Box>

        <PlanInfoBox info={model.planInfoProps} hideEdit />

        <Pressable onPress={() => setSetEmojiSheet(true)}>
          <Box>
            <XStack gap="$space.medium" style={{ alignItems: "center" }}>
              <Label color={"$muted"}>Icon</Label>
              <Label>{model.data.template.emoji}</Label>
            </XStack>
          </Box>
        </Pressable>

        <Box>
          <XStack gap="$space.medium" alignItems={"center"}>
            <Label color={"$muted"}>Title</Label>
            <TitleTextarea
              icon={Pencil}
              placeholder="Set title"
              defaultValue={model.data.template.title ?? ""}
              onSubmitEditing={(e) => {
                model.setTemplateData({ title: e.nativeEvent.text });
              }}
              submitOnEnter
              flex={1}
            />
          </XStack>
        </Box>

        <Box>
          <Label color={"$muted"}>Description</Label>
          <EditorForm
            id="description"
            onSave={(data) =>
              model?.setTemplateData({ descriptionYooptaEditor: data })
            }
          />
        </Box>

        <Box>
          <Label color={"$muted"}>Background theme</Label>

          {showColors && (
            <Box>
              <XStack
                width={"100%"}
                flexWrap="wrap"
                gap="$small"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Palette size={"$medium"} />
                {TagColorsAvailable.map((color) => {
                  const isCurrent =
                    color.color === model.planInfoProps.backgroundColor;

                  return (
                    <TouchableOpacity
                      key={color.color}
                      onPress={() => {
                        model.setTemplateData({
                          backgroundColor: color.color,
                          coverUrl: null,
                        });
                      }}
                    >
                      <Circle
                        key={color.color}
                        backgroundColor={color.color}
                        size="$medium"
                      >
                        {isCurrent && <CheckCircle color={color.textColor} />}
                      </Circle>
                    </TouchableOpacity>
                  );
                })}
              </XStack>
            </Box>
          )}

          <XStack
            width={"100%"}
            gap={"$medium"}
            marginTop={"$small"}
            alignItems={"center"}
          >
            <View flex={1}>{selectCoverButton}</View>
            <Label color={"$muted"}>or</Label>

            <View flex={1}>
              <Button
                icon={showColors ? Check : Palette}
                onPress={() => {
                  setShowColors(!showColors);
                }}
              >
                {showColors ? "Close" : "Color"}
              </Button>
            </View>
          </XStack>
        </Box>

        <Box>
          <Title>2. Outro</Title>

          <Body>
            When your users are done, make sure they feel proud and give them
            some final information if needed.
          </Body>

          <PlanQuickstartOutroInfo
            title={model.data.template.onTemplateComplete.outroTitle}
            description={
              model.data.template.onTemplateComplete.outroDescription
            }
            editOptions={{
              onTitleEdit: (text) => {
                model.setTemplateData({
                  onTemplateComplete: {
                    ...model.data.template.onTemplateComplete,
                    outroTitle: text,
                  },
                });
              },
              onDescriptionEdit: (text) => {
                model.setTemplateData({
                  onTemplateComplete: {
                    ...model.data.template.onTemplateComplete,
                    outroDescription: text,
                  },
                });
              },
            }}
          />
        </Box>

        <Box>
          <Title>3. Share</Title>
          <Body>
            Please note that new edits will only be visible in new Planiras. So
            make it right the first time!
          </Body>
          <Separator />
          <Body>
            Start sharing your personal link below to start the fun, but try it
            out yourself first!
          </Body>
          <Input
            multiline={false}
            preventNewLine
            disabled
            type="border"
            defaultValue={urlToShare}
          />

          <XStack gap={"$medium"} alignItems={"center"}>
            <Info color={"$muted"} />
            <Body>Template used:</Body>
            <Label>{model.data.timesUsed} times</Label>
          </XStack>

          <Button
            icon={Copy}
            onPress={() => {
              navigator.clipboard.writeText(urlToShare);

              toast.show("Share url copied to your clipboard", {
                duration: 2500,
              });
            }}
          >
            Copy link
          </Button>
        </Box>

        <Box>
          <XStack gap={"$small"} justifyContent={"space-between"}>
            <Title>4. Add widget (optional)</Title>

            <Pill type={PillType.Primary}>
              <Body color={"#000"}>CODING REQUIRED</Body>
            </Pill>
          </XStack>

          <Body>
            To ease up the usage of your awesome template feel free to insert
            this code block where you want the button to shine.
          </Body>

          <BodyTextarea
            icon={PencilLine}
            type={"border"}
            value={widgetTitle}
            placeholder={"Set widget title..."}
            onChangeText={(t) => {
              setWidgetTitle(t);
            }}
          />

          <BodyTextarea
            icon={Copy}
            type={"border"}
            onIconPress={() => {
              toast.show("Copied to clipboard!");
              navigator.clipboard.writeText(widgetCode);
            }}
            value={widgetCode}
          />

          <WidgetPreview widgetTitle={widgetTitle} uid={model.data.uid} />
        </Box>

        <Box>
          <Title>5. Repeat</Title>
          <Body>
            If you ever want to change something, or just find the share url
            again make sure to keep a link to this page somewhere safe. Only the
            one with the link can edit this template. 🔒
          </Body>

          <Label>Happy Planira!</Label>
        </Box>
      </YStack>

      <EmojiPicker
        sheetId="emoji"
        open={showEmojiSheet}
        onOpenChange={(open: boolean) => setSetEmojiSheet(open)}
        onSelect={(emoji) => {
          model.setTemplateData({ emoji: emoji.emoji });
          setSetEmojiSheet(false);
        }}
      />

      <Footer />
    </Screen>
  );
});
